import React, { useState, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "./contexts/LanguageContext";
import usePageMeta from "./seo/usePageMeta";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProgressiveImage, { toSrcSet } from "./components/ProgressiveImage";
import "./PaintingsPage.css";

import paintingImages from "./media/paintings";
import paintings from "./content/paintings.json";

// Catalogue order, source file (see src/media/paintings-src) and fallback title
const paintingsData = paintings.map((p) => ({ ...p, image: paintingImages[p.file], description: "" }));

// Rendered width of one grid cell; mirrors the columns, gap and padding in PaintingsPage.css
const GRID_SIZES = [
  "(max-width: 480px) calc(100vw - 40px)",
  "(max-width: 768px) calc((100vw - 60px) / 2)",
  "(max-width: 1200px) calc((100vw - 80px) / 3)",
  "(max-width: 1600px) calc((100vw - 120px) / 5)",
  "296px",
].join(", ");
const FIRST_ROW = 5;

// The overlay image is capped by max-width and max-height (see .painting-overlay-image),
// so its rendered width depends on the painting's aspect ratio
const overlaySizes = ({ width, height }) => {
  const ratio = (width / height).toFixed(3);
  return `(max-width: 768px) min(75vw, 65vh * ${ratio}), min(90vw, 80vh * ${ratio})`;
};

function PaintingsPage() {
  const location = useLocation();
  const [selectedId, setSelectedId] = useState(null);
  const { t } = useLanguage();
  usePageMeta("paintings", "/paintings");

  // Start at the top, before paint. "instant" overrides the CSS scroll-behavior: smooth,
  // which would otherwise sweep through the whole grid and trigger every lazy image.
  useLayoutEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Clear any hash
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const handlePaintingClick = (e, id) => {
    e.stopPropagation(); // Prevent click from bubbling to container
    setSelectedId(selectedId === id ? null : id);
  };

  const handleContainerClick = () => {
    setSelectedId(null); // Deselect when clicking outside paintings
  };

  const selectedPainting = paintingsData.find(p => p.id === selectedId);

  return (
    <div className="paintings-page">
      <Navbar theme="light" />

      <div className="paintings-container" onClick={handleContainerClick}>
        <h1 className="paintings-title">{t("paintings.title")}</h1>
        <p className="paintings-intro">{t("paintings.intro")}</p>

        <div className={`paintings-grid ${selectedId ? "has-selection" : ""}`}>
          {paintingsData.map((painting, index) => (
            <div
              key={painting.id}
              className="painting-card"
              onClick={(e) => handlePaintingClick(e, painting.id)}
            >
              <div className="painting-image-wrapper">
                <ProgressiveImage
                  image={painting.image}
                  sizes={GRID_SIZES}
                  alt={`${t(`paintingsList.${painting.id}.title`, painting.title)} – ${t("paintings.altSuffix")}`}
                  className="painting-image"
                  isPriority={index < FIRST_ROW}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Selected Painting Overlay */}
        {selectedPainting && (
          <div className="painting-overlay">
            <div className="painting-overlay-content">
              {/* No width/height attributes: they would stop max-width/max-height from keeping the aspect ratio */}
              <picture>
                <source
                  type="image/avif"
                  srcSet={toSrcSet(selectedPainting.image.avif)}
                  sizes={overlaySizes(selectedPainting.image)}
                />
                <img
                  src={selectedPainting.image.webp[0][0]}
                  srcSet={toSrcSet(selectedPainting.image.webp)}
                  sizes={overlaySizes(selectedPainting.image)}
                  alt={`${t(`paintingsList.${selectedPainting.id}.title`, selectedPainting.title)} – ${t("paintings.altSuffix")}`}
                  decoding="async"
                  className="painting-overlay-image"
                />
              </picture>
              <div className="painting-overlay-info">
                <h3 className="painting-overlay-title">{t(`paintingsList.${selectedPainting.id}.title`, selectedPainting.title)}</h3>
                {t(`paintingsList.${selectedPainting.id}.desc`, selectedPainting.description) && (
                  <p className="painting-overlay-description">{t(`paintingsList.${selectedPainting.id}.desc`, selectedPainting.description)}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default PaintingsPage;
